import { useState } from "react";
import DataPanel from "../DataPanel";
import PricingGroupElementDetails from "./PricingGroupElementDetails";
import PricingGroupSelection from "./PricingGroupSelection";
import { calculateUnitPriceBasingOnKilogramPrice } from "../../model/Materials";
import { MUIDataTableColumn } from "mui-datatables";
import { Box, Button } from "@material-ui/core";
import usePricingGroupApi, { PricingGroup } from "../../api/PricingGroupApi";

interface PricingGroupElementRow {
  id: string;
  material: string;
  unitProfitMargin: string;
  unitCustomerPrice: string;
  calculatedUnitPrice: CalculatedUnitPriceContext
};

interface CalculatedUnitPriceContext {
  pricingGroupId: number;
  pricingGroupElementId: number;
  calculatedUnitPrice: number|null;
  unitCustomerPrice: number;
  refreshView: () => void;
}

const convertToPricingGroupElementRow = (pricingGroupId: number, data: any, refreshView: () => void): PricingGroupElementRow => {
  const pricingGroupElementId = data.id;
  const material = data.material;
  const unitProfitMargin = data.unitProfitMargin;
  const calculatedUnitCost = calculateUnitPriceBasingOnKilogramPrice(material);
  const unitCustomerPrice = data.unitCustomerPrice;
  const calculatedUnitPrice
    = calculatedUnitCost === undefined || unitProfitMargin === undefined
      ? null
      : Number((calculatedUnitCost + unitProfitMargin).toFixed(2));
  return {
    id: pricingGroupElementId,
    material: data.material.name,
    unitProfitMargin,
    unitCustomerPrice,
    calculatedUnitPrice: {
      pricingGroupId,
      pricingGroupElementId,
      calculatedUnitPrice,
      unitCustomerPrice,
      refreshView
    }
  }
};

const PricingGroupPanel = () => {
  const pricingGroupApi = usePricingGroupApi();
  const [lastViewRefresh, setLastViewRefresh] = useState<Date>();
  const [pricingGroup, setPricingGroup] = useState<PricingGroup>();

  const handlePricingGroupSelected = (pricingGroup: PricingGroup) => setPricingGroup(pricingGroup);

  const refreshView = () => setLastViewRefresh(new Date());

  const updatePricingGroupElementUnitPrice = (
    event: React.MouseEvent<HTMLElement>,
    pricingGroupId: number,
    pricingGroupElementId: number,
    unitPrice: number|null
  ) => {
    event.stopPropagation();
    return unitPrice === null
      ? Promise.resolve()
      : pricingGroupApi
        .updatePricingGroupElementUnitCustomerPrice(pricingGroupId, pricingGroupElementId, unitPrice);
  };

  const renderCalculatedUnitPriceCellBody = (context: CalculatedUnitPriceContext) => {
    const calculatedUnitPrice = context.calculatedUnitPrice;
    return <Box display='flex' flexDirection='row' justifyContent='flex-start'>
      <div style={{ paddingTop: 8, paddingRight: 8, width: 60, textAlign: 'end' }}>{ calculatedUnitPrice }</div>
      <Button
        disabled={ calculatedUnitPrice === null || Number(context.unitCustomerPrice.toFixed(2)) === calculatedUnitPrice }
        color='primary'
        onClick={ (event) =>
          updatePricingGroupElementUnitPrice(event, context.pricingGroupId, context.pricingGroupElementId, calculatedUnitPrice)
            .then(context.refreshView) }
        style={{ zIndex: 200 }}
        variant='contained'
      >
          Nadpisz zapisaną cenę dla klienta
      </Button>
    </Box>
  };

  const columns: MUIDataTableColumn[] = [
    {
      name: 'material',
      label: 'Materiał',
      options: {}
    },
    {
      name: 'unitProfitMargin',
      label: 'Marża na centymetr',
      options: {}
    },
    {
      name: 'unitCustomerPrice',
      label: 'Zapisana cena dla klienta',
      options: {}
    },
    {
      name: 'calculatedUnitPrice',
      label: 'Cena wyliczona na podstawie aktualnej wagi i marży',
      options: {
        customBodyRender: renderCalculatedUnitPriceCellBody
      }
    }
  ];

  const renderPricingGroupElementsDataPanel = () =>
    pricingGroup === undefined
      ? <main style={{ paddingLeft: 10 }}><h2>Wybierz grupę cenową</h2></main>
      : <DataPanel
          title={`Wpisy do grupy cenowej ${pricingGroup.name}`}
          resource={`pricing-groups/${pricingGroup.id}/elements`}
          columns={columns}
          convert={pricingGroupElementData => convertToPricingGroupElementRow(pricingGroup.id, pricingGroupElementData, refreshView)}
          renderDetails={(props) => <PricingGroupElementDetails { ...props } pricingGroup={pricingGroup} />}
        />
  ;

  return <>
    <PricingGroupSelection onPricingGroupSelected={ handlePricingGroupSelected } />
    { renderPricingGroupElementsDataPanel() }
  </>
};

export default PricingGroupPanel;